// Third Party Libraries Imports
import React, { ReactElement, useEffect, useRef, useState } from "react";
import { Virtual, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

// Import Swiper styles
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { formatNumber, getGroupByData } from "src/utils/helper/functions";
import moment from "moment";
import clsx from "clsx";
import TwoBarsChart from "./two-bars-chart";
import TwoLineChart from "./two-line-chart";

const options = {
  responsive: true,
  plugins: {
    legend: {
      display: false,
    },
    title: {
      display: false,
    },
  },
  scales: {
    x: {
      grid: {
        display: false,
      },
    },
    y: {
      // min: -2,
      // max: 10,
      grid: {
        color: (context: any) => {
          return context.tick.value === 0 ? "#CCC" : "transparent";
        },
      },
    },
  },
};

type PerformanceSwiperProps = {
  performanceData: Performance[];
  onDetailsClicked?: () => void;
  performanceDetails?: boolean;
  selectedAccount: any;
  selectedPortfolio: any;
};

const PerformanceSwiper: React.FC<PerformanceSwiperProps> = ({
  performanceData,
  onDetailsClicked,
  performanceDetails = true,
  selectedAccount,
  selectedPortfolio,
}: PerformanceSwiperProps): ReactElement => {
  const [slides, setSlides] = useState<any>([]);
  const [data, setData] = useState<any>([]);
  const [activeTab, setActiveTab] = useState<string>("yearly");

  useEffect(() => {
    setData([]);
    const groupedData = getGroupByData(performanceData, "portfolioCode");

    Object.entries(groupedData)?.map(([key, value]: any, index) => {
      setData((previous: any) => [
        ...previous,
        {
          labels: value.map((item: any) =>
            moment(item.performanceDate).format("MMM")
          ),
          portfolioCode: value[index]?.portfolioCode,
          ytd: value[value?.length - 1]?.portfolioPerformancePctYtd,
          mtd: value[value?.length - 1]?.portfolioPerformancePct,
          datasets: [
            {
              label: "Performance",
              data: value.map((item: any) =>
                activeTab === "monthly"
                  ? item?.portfolioPerformancePct || 0
                  : item?.portfolioPerformancePctYtd || 0
              ),
              borderColor: "#002F6C",
              backgroundColor: "#002F6C",
              borderWidth: activeTab === "monthly" ? 0 : 2,
              barThickness: 10,
              tension: 0.5,
              pointRadius: 3,
            },
            {
              label: "Benchmark",
              data: value.map((item: any) =>
                activeTab === "monthly"
                  ? item?.benchmarkPerformancePct
                  : item?.benchmarkPerformancePctYtd
              ),
              borderColor: "#00BAF2",
              backgroundColor: "#00BAF2",
              borderWidth: activeTab === "monthly" ? 0 : 2,
              barThickness: 10,
              tension: 0.5,
              pointRadius: 3,
            },
          ],
        },
      ]);
    });
  }, [performanceData, activeTab]);

  useEffect(() => {
    setSlides(
      Array.from({ length: data?.length }).map((_, index) =>
        activeTab === "monthly" ? (
          <TwoBarsChart key={index} data={data[index]} options={options} />
        ) : (
          <TwoLineChart key={index} data={data[index]} options={options} />
        )
      )
    );
  }, [data, activeTab]);

  const swiperRef = useRef<any>(null);

  const handleNext = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      const swiper = swiperRef.current.swiper;
      if (swiper.isEnd) {
        swiper.slideTo(0);
      } else {
        swiper.slideNext();
      }
    }
  };

  const handlePrev = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slidePrev();
    }
  };

  return (
    <div className=" bg-white rounded-lg flex flex-grow flex-col w-full overflow-hidden">
      {data?.length ? (
        <>
          {selectedAccount?.id &&
            !selectedPortfolio?.id &&
            selectedAccount?.portfolios?.length > 1 && (
              <div className="custom-buttons relative">
                <div
                  onClick={handlePrev}
                  className="cursor-pointer bg-[#F5F6FA] rounded-full w-[30px] h-[30px] flex items-center justify-center absolute end-12 z-[1000] top-0"
                >
                  <img
                    src="/images/right-arrow.svg"
                    alt="arrow-left"
                    width={8}
                    height={8}
                    className={clsx("rotate-180")}
                  />
                </div>
                <div
                  onClick={handleNext}
                  className="cursor-pointer bg-[#F5F6FA] rounded-full w-[30px] h-[30px] flex items-center justify-center absolute end-1 z-[1000] top-0"
                >
                  <img
                    src="/images/right-arrow.svg"
                    alt="arrow-left"
                    width={8}
                    height={8}
                  />
                </div>
              </div>
            )}

          <Swiper
            ref={swiperRef}
            modules={[Virtual, Navigation, Pagination]}
            slidesPerView={1}
            centeredSlides={false}
            spaceBetween={0}
            style={{ height: "fit-content", margin: "0", overflow: "hidden" }}
            pagination={{
              type: "fraction",
            }}
            // autoplay={{
            //   delay: 5000,
            //   disableOnInteraction: false,
            //   pauseOnMouseEnter: true,
            // }}
            // navigation={true}
            virtual
          >
            {slides?.map((slideContent: any, index: number) => (
              <SwiperSlide
                key={slideContent}
                virtualIndex={index}
                // className="h-[250px]"
              >
                <div className="flex flex-col w-full h-full">
                  <div className="items-center gap-3 inline-flex justify-between mb-2">
                    {performanceDetails && (
                      <div className=" opacity-90 text-[#2a2a2a] text-xs font-bold">
                        Portfolio #{data[index]?.portfolioCode} -{" "}
                        {activeTab === "monthly"
                          ? "MTD Performance"
                          : "YTD Performance"}{" "}
                        {activeTab === "monthly"
                          ? data[index]?.mtd >= 0
                            ? formatNumber(Math.abs(data[index]?.mtd), 2)
                            : `(${formatNumber(Math.abs(data[index]?.mtd), 2)})`
                          : data[index]?.ytd >= 0
                          ? formatNumber(Math.abs(data[index]?.ytd), 2)
                          : `(${formatNumber(Math.abs(data[index]?.ytd), 2)})`}
                        %
                      </div>
                    )}
                  </div>
                  {slideContent}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </>
      ) : (
        ""
      )}
      <div className="self-stretch inline-flex justify-between items-center mt-3">
        <div className="bg-sky-900 rounded-lg outline outline-1 outline-offset-[-1px] outline-sky-900 flex justify-end items-center gap-0.5">
          <div
            onClick={() => setActiveTab("monthly")}
            className={clsx(
              "cursor-pointer px-1 py-2  rounded-lg outline outline-1 outline-offset-[-1px]  flex justify-center items-center",
              activeTab === "monthly"
                ? "outline-sky-900 bg-blue-500 text-white"
                : "outline-transparent bg-white text-blue-500"
            )}
          >
            <div
              className={clsx(
                "w-14 text-center justify-center0 text-xs font-semibold font-['Montserrat'] capitalize",
                activeTab === "monthly" ? "text-white" : "text-blue-500"
              )}
            >
              Monthly
            </div>
          </div>
          <div
            onClick={() => setActiveTab("yearly")}
            className={clsx(
              "cursor-pointer px-1 py-2  rounded-lg outline outline-1 outline-offset-[-1px]  flex justify-center items-center",
              activeTab === "yearly"
                ? "outline-sky-900 bg-blue-900 text-white"
                : "outline-transparent bg-white text-blue-500"
            )}
          >
            <div
              className={clsx(
                "w-14 text-center justify-center0 text-xs font-semibold font-['Montserrat'] capitalize",
                activeTab === "yearly" ? "text-white" : "text-blue-500"
              )}
            >
              Yearly
            </div>
          </div>
        </div>
        <div className="flex justify-end items-center gap-2.5">
          <div className="flex justify-start items-center gap-1">
            <div className="w-3.5 h-3.5 bg-sky-900 rounded-full" />
            <div className="justify-center text-zinc-800 text-xs font-medium font-['Montserrat'] capitalize">
              Portfolio
            </div>
          </div>
          <div className="flex justify-start items-center gap-1">
            <div className="w-3.5 h-3.5 bg-sky-500 rounded-full" />
            <div className="justify-center text-zinc-800 text-xs font-medium font-['Montserrat'] capitalize">
              Benchmark
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceSwiper;
